import React, {
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from "react";
import {
  Autocomplete,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import Customer from "../models/Customer";
import { AppContext } from "../AppContextProvider";
import CustomerDialog from "./customers/CustomerDialog";
import ReadOnlyTextField from "./rfqs/ReadOnlyTextField";
import { copyTextToClipboard } from "../utils/copyTextToClipboard";

export interface CustomerAutocompleteRef {
  focus: () => void;
}

export interface CustomerAutocompleteProps {
  customer?: Customer;
  /**
   * Unmatched / extracted name when no directory client is linked
   * (shows in the field with + to create, same pattern as Buyer).
   */
  customerName?: string;
  onCustomerSelected: (
    customer: Customer | undefined,
    isNew: boolean,
    confirmed: boolean
  ) => void;
  onBlur?: () => void;
  label?: string;
  required?: boolean;
  readOnly?: boolean;
  showCopyButton?: boolean;
  sx?: object;
}

const emptyCustomerOption: Customer = {
  id: 0,
  name: "",
  shipAcctType: "",
  shipAcctNum: "",
  contacts: [],
  qcCodes: [],
  shippingAccounts: [],
};

const buildEmptyCustomerWithName = (name?: string): Customer => ({
  ...emptyCustomerOption,
  name: name?.trim() ?? "",
});

const CustomerAutocomplete = forwardRef<
  CustomerAutocompleteRef,
  CustomerAutocompleteProps
>(
  (
    {
      customer,
      customerName,
      onCustomerSelected,
      onBlur,
      label = "Client",
      required = true,
      readOnly = false,
      showCopyButton = false,
      sx = {},
    },
    ref
  ) => {
    const { customers } = useContext(AppContext);
    const textFieldRef = useRef<HTMLInputElement>(null);

    const prevSelectedCustomerRef = useRef(customer);
    const lastLocalCustomerRef = useRef<Customer>(
      customer ?? buildEmptyCustomerWithName(customerName)
    );
    const [currentlySelectedCustomer, setCurrentlySelectedCustomer] =
      useState<Customer>(
        () => customer ?? buildEmptyCustomerWithName(customerName)
      );
    const [error, setError] = useState<string>("");
    const [showCreateDialog, setShowCreateDialog] = useState(false);

    const sortedCustomers = useMemo(() => {
      return [...customers].filter((c) => c.id !== 1).sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
    }, [customers]);

    useEffect(() => {
      if (customer === undefined) {
        const lastLocal = lastLocalCustomerRef.current;
        const isTempTyping =
          lastLocal?.id === 0 && lastLocal?.name?.trim().length > 0;
        const unmatchedName = customerName?.trim() ?? "";

        // Prefer extracted/unmatched name (e.g. SAM.gov agency) when no linked client.
        if (unmatchedName) {
          const matched = sortedCustomers.find(
            (c) => c.name.toLowerCase() === unmatchedName.toLowerCase()
          );
          if (matched) {
            setCurrentlySelectedCustomer(matched);
            lastLocalCustomerRef.current = matched;
          } else if (
            !isTempTyping ||
            lastLocal.name.trim().toLowerCase() === unmatchedName.toLowerCase()
          ) {
            const temp = buildEmptyCustomerWithName(unmatchedName);
            setCurrentlySelectedCustomer(temp);
            lastLocalCustomerRef.current = temp;
          }
          prevSelectedCustomerRef.current = undefined;
          setError("");
          return;
        }

        if (prevSelectedCustomerRef.current !== undefined && !isTempTyping) {
          setCurrentlySelectedCustomer(emptyCustomerOption);
          lastLocalCustomerRef.current = emptyCustomerOption;
          prevSelectedCustomerRef.current = undefined;
          setError("");
        }
        return;
      }

      prevSelectedCustomerRef.current = customer;
      const fromOptions = customers.find((c) => c.id === customer.id);
      setCurrentlySelectedCustomer(fromOptions ?? customer);
      lastLocalCustomerRef.current = fromOptions ?? customer;
      setError("");
    }, [customer, customers, customerName, sortedCustomers]);

    useImperativeHandle(ref, () => ({
      focus: () => textFieldRef.current?.focus(),
    }));

    const showAddButton =
      !readOnly &&
      currentlySelectedCustomer.id === 0 &&
      currentlySelectedCustomer.name.trim().length > 0;

    const getOptionLabel = (option: string | Customer): string => {
      if (typeof option === "string") return option;
      return option.id !== 1 ? option.name : "";
    };

    const notifyCustomerSelected = (customer: Customer, isNew: boolean, confirmed: boolean) => {
      // If user typed a non-matching value => temp id=0 => notify undefined.
      // Also treat id=1 as invalid and notify undefined.
      if (customer.id === 0 || customer.id === 1) {
        onCustomerSelected(undefined, isNew, confirmed);
        return;
      }
      onCustomerSelected(customer, isNew, confirmed);
    };

    const handleCustomerChanged = (customer: Customer, isNew: boolean, confirmed: boolean) => {
      setCurrentlySelectedCustomer(customer);
      lastLocalCustomerRef.current = customer;
      notifyCustomerSelected(customer, isNew, confirmed);
      if (error) setError("");
    };

    const inputValue = getOptionLabel(currentlySelectedCustomer);

    // Filter using our inputValue so the dropdown stays in sync on first keypress
    // (Autocomplete's internal state can lag one frame when controlled)
    const filterOptions = (options: Customer[]) => {
      const query = inputValue.trim().toLowerCase();
      if (query === "") return options;
      return options.filter((opt) =>
        getOptionLabel(opt).toLowerCase().includes(query)
      );
    };

    const handleInputChange = (
      _: React.SyntheticEvent,
      newInputValue: string
    ) => {
      const trimmed = newInputValue.trim();

      // Cleared input -> propagate empty temp customer
      if (trimmed.length === 0) {
        handleCustomerChanged({ ...emptyCustomerOption }, false, false);
        return;
      }

      const existing = sortedCustomers.find(
        (c) => c.name.toLowerCase() === trimmed.toLowerCase()
      );

      // Only switch to existing customer when input has no leading/trailing spaces.
      // This keeps typing e.g. "Acme Corp " from snapping to the option.
      if (existing && newInputValue === trimmed) {
        handleCustomerChanged(existing, false, false);
      } else {
        handleCustomerChanged({
            ...emptyCustomerOption,
            name: newInputValue,
          },
          false,
          false
        );
      }
    };

    const handleAddCustomerClick = () => {
      setShowCreateDialog(true);
    };

    const handleCreateDialogClose = (createdCustomer?: Customer) => {
      if (createdCustomer) {
        setCurrentlySelectedCustomer(createdCustomer);
        notifyCustomerSelected(createdCustomer, true, true);
        setError("");
      }
      setShowCreateDialog(false);
    };

    const handleTextFieldBlur = () => {
      if (currentlySelectedCustomer.id === 0 || currentlySelectedCustomer.id === 1) {
        onCustomerSelected(undefined, false, false);
      }
      onBlur?.();
    };


    if (readOnly) {
      return (
        <ReadOnlyTextField
          fullWidth
          inputRef={textFieldRef}
          label={label}
          value={currentlySelectedCustomer?.name ?? ""}
          variant="standard"
          sx={sx}
          error={!!error}
          helperText={error}
          canCopy={showCopyButton}
        />
      );
    }

    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={sx}>
        <Autocomplete
          freeSolo
          disableClearable
          options={sortedCustomers}
          getOptionLabel={getOptionLabel}
          filterOptions={filterOptions}
          autoHighlight
          value={currentlySelectedCustomer}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onChange={(_, newValue, reason) => {
            if (typeof newValue === "string") {
              const trimmed = newValue.trim();  
              if (!trimmed) {
                handleCustomerChanged({ ...emptyCustomerOption }, false, false);
              } else {
                handleCustomerChanged({
                  ...emptyCustomerOption,
                  name: trimmed,
                }, false, false);
              }
              return;
            }

            if (newValue) {
              handleCustomerChanged(newValue, false, reason === "selectOption");
            } else {
              handleCustomerChanged({ ...emptyCustomerOption }, false, false);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={textFieldRef}
              label={label}
              variant="standard"
              required={required}
              error={!!error}
              helperText={error}
              title={
                showCopyButton && inputValue.length > 0
                  ? "Double-click to copy"
                  : undefined
              }
              onDoubleClick={
                showCopyButton && inputValue.length > 0
                  ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      void copyTextToClipboard(inputValue);
                    }
                  : undefined
              }
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {params.InputProps?.endAdornment}
                      {showAddButton ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={handleAddCustomerClick}
                            aria-label="Create customer"
                            sx={{ padding: 0 }}
                          >
                            <Add />
                          </IconButton>
                        </InputAdornment>
                      ) : null}
                    </>
                  ),
                },
              }}
            />
          )}
          sx={{ flexGrow: 1 }}
          onBlur={handleTextFieldBlur}
        />
        {showCreateDialog && (
          <CustomerDialog
            customerName={currentlySelectedCustomer.name.trim()}
            onClose={handleCreateDialogClose}
            saveOnClose={true}
          />
        )}
      </Stack>
    );
  }
);

CustomerAutocomplete.displayName = "CustomerAutocomplete";

export default CustomerAutocomplete;

